/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable new-cap */
import * as Sentry from '@sentry/react';
import {AxiosError} from 'axios';
import {Effect, Schedule, Duration, Either} from 'effect';
import {sleep} from 'effect/Clock';

import {CompletePercentageFunc} from 'shared/helpers/api';

import {BadGatewayError, GatewayTimeoutError, HttpError, NetworkError, EffectError} from './errors';

export type PollingConfig<R extends {status: string; progressPercent?: number}> = {
  importId: string;
  maxWaitSec: number;
  completePercentage?: CompletePercentageFunc;
  fetcher: () => Promise<{data: R}>;
};

// Helper function to determine if an error is retryable
export const isRetryableError = (error: EffectError): boolean => {
  if (error instanceof BadGatewayError || error instanceof GatewayTimeoutError) {
    return true;
  }
  if (error instanceof HttpError) {
    // Only retry specific HTTP status codes
    return error.statusCode === 502 || error.statusCode === 504;
  }
  if (error instanceof NetworkError) {
    return true;
  }

  // For AxiosError or other Error types
  const axiosError = error as AxiosError;
  return (
    !axiosError.response || // Network error
    axiosError.code === 'ECONNABORTED' || // Timeout
    axiosError.response?.status === 502 ||
    axiosError.response?.status === 504
  );
};

const mapError = (error: unknown, context: string): EffectError => {
  const axiosError = error as AxiosError;

  if (axiosError.response) {
    const statusCode = axiosError.response.status;

    if (statusCode === 502) {
      return new BadGatewayError({
        message: `Bad Gateway error (502) in ${context}`,
        cause: error as Error,
      });
    }

    if (statusCode === 504) {
      return new GatewayTimeoutError({
        message: `Gateway Timeout error (504) in ${context}`,
        cause: error as Error,
      });
    }

    return new HttpError({
      statusCode,
      message: `HTTP error ${statusCode} in ${context}`,
      cause: error as Error,
    });
  }

  // Network errors or ECONNABORTED
  if (!axiosError.response || axiosError.code === 'ECONNABORTED') {
    return new NetworkError({
      message: `Network error in ${context}: ${axiosError.message}`,
      cause: error as Error,
    });
  }

  return new NetworkError({
    message: `Unknown error in ${context}: ${error instanceof Error ? error.message : String(error)}`,
    cause: error instanceof Error ? error : undefined,
  });
};

// Convert a Promise-based fetcher to an Effect
const createFetcherEffect = <R extends {status: string; progressPercent?: number}>(
  fetcher: () => Promise<{data: R}>,
) => {
  return Effect.flatMap(
    Effect.tryPromise({
      try: fetcher,
      catch: (error) => {
        const _error = mapError(error, 'PollingService.fetch');
        return _error;
      },
    }),
    (result) => Effect.succeed(result),
  );
};

const effect = Effect.gen(function* () {
  // Poll function that exactly mimics the behavior of existing pollResults function
  // but adds retry logic for errors
  const poll = <R extends {status: string; progressPercent?: number}>(
    config: PollingConfig<R>,
  ): Effect.Effect<R, EffectError, never> => {
    const {maxWaitSec, completePercentage, fetcher} = config;

    return Effect.gen(function* (_) {
      const fetcherEffect = createFetcherEffect(fetcher);

      const maxRetries = 5;
      const retryPolicy = Schedule.exponential(Duration.seconds(1), 1.5)
        .pipe(Schedule.compose(Schedule.recurs(maxRetries)))
        .pipe(
          Schedule.whileInput((error: EffectError) => {
            const shouldRetry = isRetryableError(error);
            if (shouldRetry) {
              Sentry.captureMessage(`Polling retry attempt due to error`, {
                level: 'warning',
                tags: {
                  context: 'PollingService.retry',
                  importId: config.importId,
                },
                extra: {
                  error,
                },
              });
            }
            return shouldRetry;
          }),
        );

      const retryableFetcher = fetcherEffect.pipe(
        Effect.retry(retryPolicy),
        Effect.catchAll((error) => Effect.fail(error)),
      );

      // Attempt to execute the retryableFetcher effect and capture the result as Either
      const respOrFailure = yield* _(Effect.either(retryableFetcher));
      // Check if the response is an error (Left variant of Either)
      const isError = Either.isLeft(respOrFailure);
      let data: R;
      if (!isError) {
        // If no error, extract the data from the Right variant
        data = respOrFailure.right.data;
      } else {
        // If an error occurred, extract it from the Left variant
        const error = respOrFailure.left;
        if (error) {
          // Propagate the error by failing the effect
          yield* _(Effect.fail(error));
          return;
        }
      }
      let iters = 1;

      // Report initial progress
      if (typeof completePercentage === 'function' && typeof data.progressPercent === 'number') {
        completePercentage(data.progressPercent);
      }

      // Poll until completion or max wait time
      while (data && (data.status === 'queued' || data.status === 'running') && iters < maxWaitSec) {
        // Wait between polls - use same delay as existing code
        const pollDelay = 5000;
        yield* _(sleep(pollDelay));

        // Fetch again with retry capability
        const response = yield* _(retryableFetcher);
        if (completePercentage && typeof response.data.progressPercent === 'number') {
          completePercentage(response.data.progressPercent);
        }
        data = response.data;
        iters += 1;
      }

      return data;
    });
  };

  // Helper to directly run the effect and return a Promise
  const runPoll = <R extends {status: string; progressPercent?: number}>(config: PollingConfig<R>): Promise<R> => {
    return Effect.runPromise(poll(config));
  };

  return {poll, runPoll} as const;
});

// Create the service class
export class PollingService extends Effect.Service<PollingService>()('PollingService', {
  effect,
}) {}
